import { ChangeDetectorRef, Component, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { FormControl } from '@angular/forms';
import { AppUtils } from '../../../shared/utilities/app-utils';
import { QnSbnService } from '../services/qn-sbn-service.service';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatMenuTrigger } from '@angular/material/menu';

export interface NotificationData {
  dateCreated: Date;
  country: string;
  type: string;
  localId: string;
  accountId: string;
  accountName: string;
  accountStatus: string;
  delivery: string;
  feedback: string;
  globalId?: string;
  dateSent?: Date;
  dateDelivered?: Date;
  dateRead?: Date;
  dateFeedback?: Date;
  deliveryFailures?: string;
  daysToOpen?: number;
  daysToSubmit?: number;
}

export interface ColumnDefinition {
  key: string;
  label: string;
  hidden: boolean;
}

@Component({
  selector: 'dl-dashboard-table',
  templateUrl: './dashboard-table.component.html',
  styleUrls: ['./dashboard-table.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class DashboardTableComponent implements OnInit {

  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatMenuTrigger) menuTrigger: MatMenuTrigger;
  @Input() dbTableData: NotificationData[] = [];
  
  dataSource = new MatTableDataSource<NotificationData>();
  searchBox = new FormControl();
  searchValue = '';
  isLoading = false;
  totalCount = 0;
  selectedRow: NotificationData | null = null;
  tempColumnState: { [key: string]: boolean } = {};

  // Default columns that should always remain visible
  defaultColumns = ['dateCreated', 'country', 'type', 'localId', 'accountId', 'accountName', 'accountStatus', 'delivery', 'feedback'];

  // Column definitions based on the specifications
  allColumns: ColumnDefinition[] = [
    { key: 'dateCreated', label: 'Date Created', hidden: false },
    { key: 'country', label: 'Country', hidden: false },
    { key: 'type', label: 'Type', hidden: false },
    { key: 'localId', label: 'Local ID', hidden: false },
    { key: 'accountId', label: 'Account ID', hidden: false },
    { key: 'accountName', label: 'Account Name', hidden: false },
    { key: 'accountStatus', label: 'Account Status', hidden: false },
    { key: 'delivery', label: 'Delivery', hidden: false },
    { key: 'feedback', label: 'Feedback', hidden: false },
    { key: 'globalId', label: 'Global ID', hidden: true },
    { key: 'dateSent', label: 'Date Sent', hidden: true },
    { key: 'dateDelivered', label: 'Date Delivered', hidden: true },
    { key: 'dateRead', label: 'Date Read', hidden: true },
    { key: 'dateFeedback', label: 'Date Feedback', hidden: true },
    { key: 'deliveryFailures', label: 'Delivery Failures', hidden: true },
    { key: 'daysToOpen', label: 'Days to Open', hidden: true },
    { key: 'daysToSubmit', label: 'Days to Submit', hidden: true }
  ];

  // Display only visible columns
  get displayedColumns(): string[] {
    return this.allColumns.filter(col => !col.hidden).map(col => col.key);
  }

  constructor(
    public appUtils: AppUtils,
    public changeDetectorRef: ChangeDetectorRef,
    public qnsbnService: QnSbnService,
  ){}

  ngOnChanges(){
    this.dataSource.data = this.dbTableData;
    this.totalCount = this.dbTableData.length;
  }
  
  ngOnInit(): void {
    this.setupFilterPredicate();
    this.initializeTempColumnState();
  }

  initializeTempColumnState(): void {
    this.allColumns.forEach(column => {
      this.tempColumnState[column.key] = !column.hidden;
    });
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
  }

  setupFilterPredicate(): void {
    this.dataSource.filterPredicate = (data: NotificationData, filter: string) => {
      const searchStr = filter.toLowerCase();
      return (
        data.dateCreated?.toString().toLowerCase().includes(searchStr) ||
        data.country?.toLowerCase().includes(searchStr) ||
        data.type?.toLowerCase().includes(searchStr) ||
        data.localId?.toLowerCase().includes(searchStr) ||
        data.accountId?.toLowerCase().includes(searchStr) ||
        data.accountName?.toLowerCase().includes(searchStr) ||
        data.accountStatus?.toLowerCase().includes(searchStr) ||
        data.delivery?.toLowerCase().includes(searchStr) ||
        data.feedback?.toLowerCase().includes(searchStr) ||
        data.globalId?.toLowerCase().includes(searchStr)
      );
    };
  }

  applyFilter(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value;
    this.searchValue = filterValue;
    this.dataSource.filter = filterValue.trim().toLowerCase();
    
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  clearSearch(): void {
    this.searchValue = '';
    this.dataSource.filter = '';
    
    // Clear the input field
    const inputElement = document.querySelector('.search-field input') as HTMLInputElement;
    if (inputElement) {
      inputElement.value = '';
    }
    
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  isColumnVisible(columnKey: string): boolean {
    const column = this.allColumns.find(col => col.key === columnKey);
    return column ? !column.hidden : false;
  }

  toggleColumn(columnKey: string): void {
    const column = this.allColumns.find(col => col.key === columnKey);
    if (column) {
      column.hidden = !column.hidden;
      this.changeDetectorRef.detectChanges();
    }
  }

  onColumnSelectionChange(columnKey: string, event: any): void {
    this.tempColumnState[columnKey] = event.checked;
  }

  getTempColumnState(columnKey: string): boolean {
    return this.tempColumnState[columnKey] !== undefined ? this.tempColumnState[columnKey] : !this.allColumns.find(col => col.key === columnKey)?.hidden;
  }

  areAllColumnsSelected(): boolean {
    return this.allColumns.every(col => this.getTempColumnState(col.key));
  }

  someColumnsSelected(): boolean {
    const selectedCount = this.allColumns.filter(col => this.getTempColumnState(col.key)).length;
    return selectedCount > 0 && selectedCount < this.allColumns.length;
  }

  toggleAllColumns(): void {
    const allSelected = this.areAllColumnsSelected();
    this.allColumns.forEach(column => {
      this.tempColumnState[column.key] = !allSelected;
    });
  }

  applyColumnChanges(): void {
    // Check if no columns would be visible
    const selectedColumns = this.allColumns.filter(col => this.getTempColumnState(col.key));
    
    if (selectedColumns.length === 0) {
      // If no columns are selected, revert to default columns
      this.allColumns.forEach(column => {
        column.hidden = this.defaultColumns.includes(column.key) ? false : true;
        this.tempColumnState[column.key] = this.defaultColumns.includes(column.key);
      });
    } else {
      // Apply the selected state normally
      this.allColumns.forEach(column => {
        column.hidden = !this.getTempColumnState(column.key);
      });
    }
    
    this.menuTrigger?.closeMenu();
    this.changeDetectorRef.detectChanges();
  }

  selectAllColumns(): void {
    this.allColumns.forEach(column => {
      column.hidden = false;
    });
    this.changeDetectorRef.detectChanges();
  }

  deselectAllColumns(): void {
    this.allColumns.forEach(column => {
      // Keep default columns visible
      if (this.defaultColumns.includes(column.key)) {
        column.hidden = false;
      } else {
        column.hidden = true;
      }
    });
    this.changeDetectorRef.detectChanges();
  }

  trackByColumn(index: number, column: ColumnDefinition): string {
    return column.key;
  }

  onPageChange(event: PageEvent): void {
    // Handle pagination changes if needed for server-side pagination
    console.log('Page changed:', event);
  }

  getStatusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'send': return 'status-send';
      case 'sent': return 'status-sent';
      case 'delivered': return 'status-delivered';
      case 'delivery failed': return 'status-delivery-failed';
      case 'reminder': return 'status-reminder';
      case 'read': return 'status-read';
      case 'feedback overdue': return 'status-feedback-overdue';
      case 'feedback received': return 'status-feedback-received';
      case 'feedback submitted': return 'status-feedback-submitted';
      case 'failed': return 'status-failed';
      default: return 'status-default';
    }
  }

  getDeliveryClass(delivery: string): string {
    switch (delivery?.toLowerCase()) {
      case 'delivered': return 'delivery-success';
      case 'failure': return 'delivery-failure';
      default: return 'delivery-default';
    }
  }

  getFeedbackClass(feedback: string): string {
    switch (feedback?.toLowerCase()) {
      case 'submitted': return 'feedback-submitted';
      case 'pending': return 'feedback-pending';
      default: return 'feedback-default';
    }
  }

  // Legacy method for backward compatibility
  searchText(searchValue: string): void {
    this.dataSource.filter = searchValue.trim().toLowerCase();
    this.changeDetectorRef.detectChanges();
  }
}
